import { z } from 'zod';

/**
 * Catálogo global de códigos de error (001 `contracts/errors.md` + 002 `research.md` R-9 /
 * `contracts/staff-api.md` + 003 `research.md` R-14 / `contracts/terminal-api.md` + 004
 * `contracts/errors.md` + 005 `contracts/errors.md` + 007 `contracts/errors-permissions-audit.md`).
 * Exactamente 44: 15 de 001, 9 del realm staff de 002, 2 del realm terminal de 003, 11 de la
 * marcación con VPP de 004, 3 de las correcciones de asistencia de 005, 2 de la verificación del
 * documento al activar (QA-6 de la 005, realm staff), 2 de la regularización de marcas (007,
 * realms staff y admin), 4 de horarios y tareo (008, realms staff y admin) y 2 de la integración
 * con sistemas externos (009, solo realm admin). Total: 50.
 * El código HTTP asociado es `ERROR_HTTP_STATUS`; el mensaje es-PE lo fija `apps/api/src/lib/errors.ts`.
 */
export const ERROR_CODES = [
  // --- 001 (realm admin) ---
  'VALIDATION_ERROR', // 400
  'UNAUTHENTICATED', // 401
  'FORBIDDEN', // 403
  'NOT_FOUND', // 404
  'CONFLICT', // 409
  'LAST_ADMIN', // 409
  'TOKEN_EXPIRED', // 410
  'TOKEN_INVALID', // 410
  'PASSWORD_WEAK', // 422
  'PASSWORD_COMPROMISED', // 422
  'INVALID_TIMEZONE', // 422
  'ACCOUNT_LOCKED', // 423
  'READ_ONLY', // 423
  'RATE_LIMITED', // 429
  'INTERNAL_ERROR', // 500
  // --- 002 (realm staff; contracts/staff-api.md "Errores del realm staff") ---
  'INVITATION_INVALID', // 410 (mismo mensaje que INVITATION_EXPIRED: respuesta neutra)
  'INVITATION_EXPIRED', // 410
  'HANDOFF_CODE_INVALID', // 410
  'DEVICE_MISMATCH', // 401
  'DEVICE_CLOCK_SKEW', // 401
  'WORKER_INACTIVE', // 403
  'COMPANY_SUSPENDED', // 423
  'REBIND_NOT_PENDING', // 409
  'PRIVACY_NOTICE_REQUIRED', // 428
  // --- 003 (realm terminal; contracts/terminal-api.md "Errores del realm terminal") ---
  'TERMINAL_CODE_INVALID', // 410 (inexistente, vencido, usado, revocado, terminal deshabilitado o sede inactiva: un solo código)
  'TERMINAL_SESSION_REVOKED', // 401 (`details.reason ∈ { REPLACED, UNLINKED, SITE_INACTIVE }`)
  // --- 004 (realm staff; contracts/errors.md, vpp-protocol §10.2) ---
  // El detalle interno (BAD_TOKEN, OUT_OF_ORDER, WINDOW_EXCEEDED, REPLAY, OWNER_MISMATCH,
  // MARKER_UNRESPONSIVE, BOOTSTRAP_STALE…) viaja solo a observabilidad, nunca en `details` (FR-023).
  // `SITE_MISMATCH` NO es un código: es un evento de observabilidad; el código es `SITE_NOT_ALLOWED`.
  'TERMINAL_UNAVAILABLE', // 409
  'TERMINAL_BUSY', // 429 (`details.retry_after_seconds`)
  'COMPANY_MISMATCH', // 403
  'SITE_NOT_ALLOWED', // 403
  'CHALLENGE_EXPIRED', // 410 (mismo mensaje que INSUFFICIENT_EVIDENCE: respuesta opaca)
  'INSUFFICIENT_EVIDENCE', // 422
  'CHALLENGE_ALREADY_USED', // 409 (mismo principal ⇒ no es error: 200 idempotente)
  'CHALLENGE_NOT_FOUND', // 404
  'ACTION_CHANGED', // 409 (`details.proposed_action`)
  'ATTENDANCE_STATE_UNRESOLVED', // 409
  'AUTONOMOUS_DISABLED', // 409 (mismo mensaje que TERMINAL_UNAVAILABLE, a propósito)
  // --- 005 (realm admin; specs/005-admin-attendance-corrections-audit/contracts/errors.md) ---
  'CONFIRMATION_REQUIRED', // 409 (`details.warnings: MarkWarningCode[]`; no se escribe nada)
  'MARK_NOT_CURRENT', // 409 (`details.status`, `details.current_mark_id`)
  'WORKER_ANONYMIZED', // 409
  // --- 005 · QA-6 (realm staff, activación): verificación de los últimos 4 del documento ---
  'DOCUMENT_VERIFICATION_REQUIRED', // 428 (privacy/handoff-code/enroll antes de verificar)
  'DOCUMENT_MISMATCH', // 422 (`details.attempts_remaining`; 0 ⇒ la invitación quedó revocada)
  // --- 007 (realms staff y admin; contracts/errors-permissions-audit.md §2) ---
  'REGULARIZATION_NOT_OPEN', // 409 (`details.status: RegularizationStatus`)
  'REGULARIZATION_UNAVAILABLE', // 409 (`details.reason: RegularizationUnavailableReason`)
  // --- 008 (realms staff y admin; contracts/errors-permissions-audit.md §2) ---
  'PERIOD_CLOSED', // 409 (`details: { closure_id, from_day, to_day }`)
  'INTEGRATION_MANAGED', // 409 (`details.entity_type`; solo realm admin)
  'JUSTIFICATION_NOT_OPEN', // 409 (`details.status: JustificationStatus`)
  'JUSTIFICATION_UNAVAILABLE', // 409 (`details.reason: JustificationUnavailableReason`)
  // --- 009 (solo realm admin; contracts/errors-permissions-audit.md §2) ---
  // `INTEGRATION_MANAGED` (008) gana `details.fields?: string[]` (campos gestionados que se intentó cambiar).
  'INTEGRATION_UNAVAILABLE', // 409 (`details.reason: IntegrationUnavailableReason`)
  'INTEGRATION_COMPANY_MISMATCH', // 409 (`details: { bound_company_name, received_company_name }`)
] as const;

export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/** Acceso tipo enum: `ErrorCode.NOT_FOUND === 'NOT_FOUND'`. */
export const ErrorCode = Object.freeze(
  Object.fromEntries(ERROR_CODES.map((code) => [code, code])) as { [K in ErrorCode]: K },
);

/**
 * Mapa HTTP por código, según contracts/errors.md (001), contracts/staff-api.md (002),
 * contracts/terminal-api.md (003) y contracts/errors.md de 004 y de 005.
 *
 * `COMPANY_SUSPENDED` es **423**, no el 403 de `vpp-protocol.md` §10.2: es el mapeo desplegado
 * desde la 002 y lo lanza el guarda del realm antes de llegar a la ruta.
 */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = Object.freeze({
  VALIDATION_ERROR: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  LAST_ADMIN: 409,
  TOKEN_EXPIRED: 410,
  TOKEN_INVALID: 410,
  PASSWORD_WEAK: 422,
  PASSWORD_COMPROMISED: 422,
  INVALID_TIMEZONE: 422,
  ACCOUNT_LOCKED: 423,
  READ_ONLY: 423,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  INVITATION_INVALID: 410,
  INVITATION_EXPIRED: 410,
  HANDOFF_CODE_INVALID: 410,
  DEVICE_MISMATCH: 401,
  DEVICE_CLOCK_SKEW: 401,
  WORKER_INACTIVE: 403,
  COMPANY_SUSPENDED: 423,
  REBIND_NOT_PENDING: 409,
  PRIVACY_NOTICE_REQUIRED: 428,
  TERMINAL_CODE_INVALID: 410,
  TERMINAL_SESSION_REVOKED: 401,
  COMPANY_MISMATCH: 403,
  SITE_NOT_ALLOWED: 403,
  CHALLENGE_NOT_FOUND: 404,
  TERMINAL_UNAVAILABLE: 409,
  CHALLENGE_ALREADY_USED: 409,
  ACTION_CHANGED: 409,
  ATTENDANCE_STATE_UNRESOLVED: 409,
  AUTONOMOUS_DISABLED: 409,
  CHALLENGE_EXPIRED: 410,
  INSUFFICIENT_EVIDENCE: 422,
  TERMINAL_BUSY: 429,
  CONFIRMATION_REQUIRED: 409,
  MARK_NOT_CURRENT: 409,
  WORKER_ANONYMIZED: 409,
  DOCUMENT_VERIFICATION_REQUIRED: 428,
  DOCUMENT_MISMATCH: 422,
  REGULARIZATION_NOT_OPEN: 409,
  REGULARIZATION_UNAVAILABLE: 409,
  PERIOD_CLOSED: 409,
  INTEGRATION_MANAGED: 409,
  JUSTIFICATION_NOT_OPEN: 409,
  JUSTIFICATION_UNAVAILABLE: 409,
  INTEGRATION_UNAVAILABLE: 409,
  INTEGRATION_COMPANY_MISMATCH: 409,
});

/**
 * Motivos estructurados de un `429 RATE_LIMITED` (`details.reason`). Solo los límites que aplica un
 * **servicio** llevan motivo; el cubo HTTP de `@fastify/rate-limit` nunca lo lleva (su 429 es
 * genérico). Staff elige el texto por motivo en vez de mostrar "Demasiadas solicitudes".
 *
 * - `DAILY_LIMIT` (002): cupo diario de solicitudes de re-vinculación agotado.
 * - `AUTONOMOUS_WORKER_INTERVAL` (004 · QA-7 de la 005): una marca autónoma por trabajador cada N s
 *   (`companies.autonomous_marks_per_worker_interval_s`, FR-045). Viaja junto a
 *   `details.retry_after_seconds`.
 */
export const RATE_LIMIT_REASONS = ['DAILY_LIMIT', 'AUTONOMOUS_WORKER_INTERVAL'] as const;
export const RateLimitReasonSchema = z.enum(RATE_LIMIT_REASONS);
export type RateLimitReason = z.infer<typeof RateLimitReasonSchema>;

/**
 * Motivos estructurados de un `409 CONFLICT` (`details.reason`, 005 · contracts/errors.md). Solo los
 * llevan las escrituras del admin sobre marcas; los demás `CONFLICT` siguen con `details.field` solo.
 *
 * - `SAME_INSTANT`: otra marca vigente del mismo trabajador en el mismo instante exacto (I-9).
 * - `IDEMPOTENCY_KEY_REUSED`: la `idempotency_key` ya creó una marca (o, en la 008, una
 *   justificación) con **otro** contenido.
 * - `SCHEDULE_CHANGED` (008): la asignación vigente de una celda del cuadro no es la que vio el
 *   cliente (`base_assignment_id`); `details.field = 'cells[i]'`.
 * - `PERIOD_OVERLAP` (008): el período a cerrar se cruza con otro cierre vigente.
 * - `REVIEW_NOT_OPEN` (009): el caso de revisión ya no está abierto (o, al reabrir, no está omitido).
 * - `ALREADY_LINKED` (009): la sede o el trabajador de Fermin ya está vinculado a otro id externo.
 * - `RUN_NOT_AWAITING_CONFIRMATION` (009): la ejecución ya no espera confirmación (confirmar/descartar).
 */
export const CONFLICT_REASONS = [
  'SAME_INSTANT',
  'IDEMPOTENCY_KEY_REUSED',
  'SCHEDULE_CHANGED',
  'PERIOD_OVERLAP',
  'REVIEW_NOT_OPEN',
  'ALREADY_LINKED',
  'RUN_NOT_AWAITING_CONFIRMATION',
] as const;
export const ConflictReasonSchema = z.enum(CONFLICT_REASONS);
export type ConflictReason = z.infer<typeof ConflictReasonSchema>;

/**
 * Motivo estructurado de `410 INVITATION_INVALID` y `410 HANDOFF_CODE_INVALID` (`details.reason`,
 * 005 · QA-9). Solo se informa cuando la invitación fue **reemplazada** por una nueva
 * (`revoke_reason = 'SUPERSEDED'`): el resto de los casos (inexistente, vencida, usada, revocada por
 * el admin) sigue sin `reason`, para no convertir el enlace ni el código en un oráculo de adivinanza.
 */
export const ACTIVATION_INVALID_REASONS = ['INVITATION_SUPERSEDED'] as const;
export const ActivationInvalidReasonSchema = z.enum(ACTIVATION_INVALID_REASONS);
export type ActivationInvalidReason = z.infer<typeof ActivationInvalidReasonSchema>;

/**
 * Motivos estructurados de `409 REGULARIZATION_UNAVAILABLE` (`details.reason`, 007 ·
 * contracts/errors-permissions-audit.md §2). Los clientes eligen el texto con
 * `RegularizationUnavailableReasonSchema.enum`, nunca comparando literales.
 *
 * - `NO_GAP`: la marca ancla ya no es vigente o ya no tiene hueco.
 * - `OUTSIDE_WINDOW`: el ancla cae fuera de `regularization_window_days` (propuesta del trabajador).
 * - `ALREADY_OPEN`: ya hay una solicitud abierta (`ASKED`/`PENDING`) para el ancla.
 * - `PROPOSALS_EXHAUSTED`: el trabajador ya usó sus `REGULARIZATION_MAX_PROPOSALS` propuestas.
 * - `WORKER_NOT_ACTIVE`: el trabajador no está `ACTIVE` (pedir desde el Admin).
 * - `TOO_OLD`: el ancla tiene más de 60 días operativos (pedir desde el Admin).
 */
export const REGULARIZATION_UNAVAILABLE_REASONS = [
  'NO_GAP',
  'OUTSIDE_WINDOW',
  'ALREADY_OPEN',
  'PROPOSALS_EXHAUSTED',
  'WORKER_NOT_ACTIVE',
  'TOO_OLD',
] as const;
export const RegularizationUnavailableReasonSchema = z.enum(REGULARIZATION_UNAVAILABLE_REASONS);
export type RegularizationUnavailableReason = z.infer<typeof RegularizationUnavailableReasonSchema>;

/**
 * Motivos estructurados de `409 JUSTIFICATION_UNAVAILABLE` (`details.reason`, 008 ·
 * contracts/errors-permissions-audit.md §2). Los clientes eligen el texto con
 * `JustificationUnavailableReasonSchema.enum`, nunca comparando literales.
 *
 * - `NOTHING_TO_JUSTIFY`: el día ya no tiene el hecho (tardanza, salida anticipada o falta).
 * - `DAY_NOT_FINAL`: falta o salida de un turno que todavía no terminó.
 * - `OUTSIDE_WINDOW`: el día cae fuera de `regularization_window_days`.
 * - `ALREADY_OPEN`: ya hay una `PENDING` del mismo día y tipo.
 * - `LIMIT_REACHED`: ya hay `JUSTIFICATION_MAX_PER_DAY_KIND` del mismo día y tipo.
 * - `WORKER_NOT_ACTIVE`: el trabajador no está `ACTIVE` (defensa en profundidad del realm).
 * - `STILL_APPLIES`: el admin intenta cerrar una que todavía aplica.
 */
export const JUSTIFICATION_UNAVAILABLE_REASONS = [
  'NOTHING_TO_JUSTIFY',
  'DAY_NOT_FINAL',
  'OUTSIDE_WINDOW',
  'ALREADY_OPEN',
  'LIMIT_REACHED',
  'WORKER_NOT_ACTIVE',
  'STILL_APPLIES',
] as const;
export const JustificationUnavailableReasonSchema = z.enum(JUSTIFICATION_UNAVAILABLE_REASONS);
export type JustificationUnavailableReason = z.infer<typeof JustificationUnavailableReasonSchema>;

/**
 * Motivos estructurados de `409 INTEGRATION_UNAVAILABLE` (`details.reason`, 009 ·
 * contracts/errors-permissions-audit.md §2). El Admin elige el texto con
 * `IntegrationUnavailableReasonSchema.enum`, nunca comparando literales.
 *
 * - `NOT_CONFIGURED`: la empresa no tiene conexión guardada.
 * - `DISABLED`: la conexión está desactivada.
 * - `SECRET_KEY_MISSING`: el servidor no tiene `INTEGRATION_SECRET_KEY` (no puede cifrar la clave).
 * - `NOT_VERIFIED`: la conexión nunca se probó con éxito (sin capacidades).
 * - `RESOURCE_NOT_OFFERED`: el sistema externo no ofrece el recurso pedido.
 * - `SYNC_DISABLED`: `INTEGRATION_SYNC_ENABLED=false` en este servidor (p. ej. Vercel).
 */
export const INTEGRATION_UNAVAILABLE_REASONS = [
  'NOT_CONFIGURED',
  'DISABLED',
  'SECRET_KEY_MISSING',
  'NOT_VERIFIED',
  'RESOURCE_NOT_OFFERED',
  'SYNC_DISABLED',
] as const;
export const IntegrationUnavailableReasonSchema = z.enum(INTEGRATION_UNAVAILABLE_REASONS);
export type IntegrationUnavailableReason = z.infer<typeof IntegrationUnavailableReasonSchema>;

/**
 * `details` de `409 INTEGRATION_MANAGED` (008; 009 añade `fields`). `entity_type` es el tipo de
 * entidad de auditoría (`'site'`, `'worker'`, `'shift_assignment'`…); `fields` lista los campos
 * gestionados por el sistema externo que el cuerpo intentó cambiar (ausente en las guardas de la 008).
 */
export const IntegrationManagedDetailsSchema = z.object({
  entity_type: z.string(),
  fields: z.array(z.string()).optional(),
});
export type IntegrationManagedDetails = z.infer<typeof IntegrationManagedDetailsSchema>;

/** `details` de `409 INTEGRATION_COMPANY_MISMATCH` (009): nombres, nunca ids ni la clave. */
export const IntegrationCompanyMismatchDetailsSchema = z.object({
  bound_company_name: z.string().nullable(),
  received_company_name: z.string().nullable(),
});
export type IntegrationCompanyMismatchDetails = z.infer<
  typeof IntegrationCompanyMismatchDetailsSchema
>;

/** Un problema de validación (`details.issues[]` para VALIDATION_ERROR). */
export const ValidationIssueSchema = z.object({
  path: z.string(),
  message: z.string(),
});
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

/**
 * Sobre de error único para toda respuesta de error de todos los realms (admin, staff, terminal).
 * `message` está en es-PE y es apto para mostrar; `details` es opcional y estructurado
 * (p. ej. `details.reason = 'REPLAYED_REQUEST'` en `DEVICE_MISMATCH`, research R-3).
 */
export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    details: z
      .object({
        field: z.string().optional(),
        issues: z.array(ValidationIssueSchema).optional(),
        retry_after_seconds: z.number().int().nonnegative().optional(),
      })
      .catchall(z.unknown())
      .optional(),
    request_id: z.string(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;
